/**
 * MeetingTimeline — project detail "Meetings" section (MVP-15).
 * See docs/phase2/mvp-architecture.md §4.7.
 *
 * Renders the canonical per-project meeting set grouped by phase (Phase 0 → Phase 4). Within each
 * phase, gate meetings are shown first (shield icon). Each card shows the meeting name, a scope
 * badge (Internal / Customer), a status badge (colour + text — never colour alone, WCAG 2.1 AA),
 * the meeting date, a CIA sentiment badge when analysis exists, and the Avoma link when available.
 * pm/sa/admin get an "Add Link" control (paste an Avoma URL) and a top-level "Sync" button.
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  CalendarClock,
  CalendarPlus,
  Shield,
  RefreshCw,
  ExternalLink,
  Link2,
  Loader2,
  Brain,
  Copy,
  Check,
} from "lucide-react";
import type { AxiosError } from "axios";

import { useApiClient } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Tooltip } from "@/components/ui/tooltip";
import { TeamsIcon } from "@/components/icons/TeamsIcon";
import { buildTeamsDeepLink } from "@/lib/teams-deep-link";
import { useAuth } from "@/contexts/AuthContext";
import { useProject } from "@/hooks/useProjects";
import { canActAsMember } from "@/lib/project-permissions";
import type { Project } from "@/types";
import { NewMeetingDialog } from "@/components/meetings/NewMeetingDialog";
import { newMeetingMessages } from "@/components/meetings/new-meeting-messages";
import { compareMeetingsInPhase } from "@/components/meetings/meeting-order";
import { phaseLabel } from "@/lib/phases";
import { Badge, type BadgeVariant } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// ─── Types (mirror packages/shared/types/meetings.ts + list-project-meetings response) ─────────
// Duplicated intentionally: the frontend is a standalone vite workspace with no dependency on
// packages/shared today (same convention as MeetingIntelligenceCard / UnifiedTeamSection).

type MeetingType = "regular" | "gate";
type MeetingScope = "internal" | "customer";
type MeetingStatus =
  | "scheduled"
  | "completed"
  | "missed"
  | "cancelled"
  | "linked";
type SentimentSignal = "positive" | "neutral" | "negative" | "at_risk";

interface CiaResultSummary {
  id: number;
  sentiment: SentimentSignal;
  confidence: number;
  topics_covered: string[];
  topics_missing: string[];
  key_observations: string[];
  analyzed_at: string;
}

interface ProjectMeeting {
  id: string;
  project_id: string;
  phase: string;
  meeting_type: MeetingType;
  meeting_name: string;
  name_template: string;
  scope: MeetingScope;
  status: MeetingStatus;
  avoma_meeting_id: string | null;
  avoma_link: string | null;
  external_link_url: string | null;
  transcript_s3_key: string | null;
  meeting_date: string | null;
  participants: string[];
  meeting_topics: string[];
  /** Canonical CASDM order (V052); null for ad-hoc meetings, which list after the canonical set. */
  sort_order?: number | null;
  cia_result_id: number | null;
  created_at: string;
  cia_result: CiaResultSummary | null;
}

export interface MeetingTimelineProps {
  projectId: string;
}

const PHASE_ORDER = ["Phase 0", "Phase 1", "Phase 2", "Phase 3", "Phase 4"];
const WRITE_ROLES = ["pm", "sa", "admin"];
/**
 * Roles allowed by POST /api/projects/{projectId}/meetings (create-meeting.ts). The server also
 * requires project membership (leadership/admin bypass) and answers 404 otherwise.
 */
const CREATE_ROLES = ["pm", "sa", "leadership", "admin"];

// Gate meetings are removed from the timeline (task 2a). After migration V028 these rows no longer
// exist in the DB, but we filter defensively in case the API returns legacy rows during rollout.
const GATE_MEETING_NAME_RE = /Gate \d Meeting/;
function isGateMeeting(m: ProjectMeeting): boolean {
  return m.meeting_type === "gate" || GATE_MEETING_NAME_RE.test(m.meeting_name);
}

const STATUS_DISPLAY: Record<
  MeetingStatus,
  { label: string; variant: BadgeVariant; strike?: boolean }
> = {
  scheduled: { label: "Scheduled", variant: "neutral" },
  completed: { label: "Completed", variant: "success" },
  missed: { label: "Missed", variant: "danger" },
  cancelled: { label: "Cancelled", variant: "neutral", strike: true },
  linked: { label: "Linked", variant: "info" },
};

const SENTIMENT_DISPLAY: Record<
  SentimentSignal,
  { label: string; variant: BadgeVariant }
> = {
  positive: { label: "Positive", variant: "success" },
  neutral: { label: "Neutral", variant: "neutral" },
  negative: { label: "Negative", variant: "warning" },
  at_risk: { label: "At Risk", variant: "danger" },
};

function formatDate(value: string | null): string {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
}

function errorMessage(err: unknown, fallback: string): string {
  const axiosErr = err as AxiosError<{ message?: string }>;
  return (
    axiosErr?.response?.data?.message ??
    (err instanceof Error ? err.message : fallback)
  );
}

// ─── Meeting detail modal ─────────────────────────────────────────────────────

function MeetingDetailModal({
  meeting,
  open,
  onOpenChange,
  projectId,
  userRole,
}: {
  meeting: ProjectMeeting | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  userRole: string;
}): JSX.Element {
  const client = useApiClient();
  const queryClient = useQueryClient();

  const reanalyze = useMutation({
    mutationFn: async () => {
      await client.post(
        `/api/projects/${projectId}/meetings/${meeting!.id}/analyze`,
      );
    },
    onSuccess: () => {
      toast.success("Analysis triggered — results will appear shortly");
      void queryClient.invalidateQueries({
        queryKey: ["project-meetings", projectId],
      });
    },
    onError: (err: unknown) => {
      const axiosErr = err as AxiosError<{ message?: string }>;
      toast.error(
        axiosErr?.response?.data?.message ?? "Failed to trigger analysis",
      );
    },
  });

  const canAnalyze = ["pm", "sa", "admin", "leadership"].includes(userRole);

  if (!meeting) return <></>;

  const status = STATUS_DISPLAY[meeting.status] ?? {
    label: meeting.status,
    variant: "neutral" as BadgeVariant,
  };
  const cia = meeting.cia_result;
  const date = formatDate(meeting.meeting_date);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {meeting.meeting_type === "gate" && (
              <Shield
                className="h-4 w-4 shrink-0 text-primary"
                aria-hidden="true"
              />
            )}
            {meeting.meeting_name}
          </DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-2 pt-1">
            <Badge variant={meeting.scope === "customer" ? "info" : "neutral"}>
              {meeting.scope === "customer" ? "Customer" : "Internal"}
            </Badge>
            <Badge variant={status.variant}>{status.label}</Badge>
            {meeting.phase && (
              <span className="text-xs text-text-secondary">
                {phaseLabel(meeting.phase)}
              </span>
            )}
            {date && (
              <span className="flex items-center gap-1 text-xs text-text-secondary">
                <CalendarClock className="h-3.5 w-3.5" aria-hidden="true" />
                {date}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Avoma title */}
        {meeting.name_template && (
          <div className="min-w-0 overflow-hidden px-1">
            <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-text-secondary">
              Avoma Meeting Title
            </p>
            <AvomaTitle value={meeting.name_template} />
            <p className="mt-1 text-[11px] text-text-secondary">
              Use this exact title when naming the meeting in Avoma so it
              auto-syncs.
            </p>
          </div>
        )}

        <div className="space-y-5 pt-2">
          {/* Avoma link */}
          {meeting.avoma_link && (
            <div>
              <p className="mb-1 text-xs font-medium uppercase tracking-wide text-text-secondary">
                Recording
              </p>
              <a
                href={meeting.avoma_link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
              >
                <ExternalLink className="h-4 w-4" aria-hidden="true" />
                Open in Avoma
              </a>
            </div>
          )}

          {/* External meeting link — only when there is no Avoma recording (§0.6 precedence). */}
          {!meeting.avoma_link && meeting.external_link_url && (
            <div>
              <p className="mb-1 text-xs font-medium uppercase tracking-wide text-text-secondary">
                Meeting invite
              </p>
              <a
                href={meeting.external_link_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
              >
                <ExternalLink className="h-4 w-4" aria-hidden="true" />
                Open meeting invite
              </a>
              <p className="mt-1 text-[11px] text-text-secondary">
                External link added manually. No recording or transcript is
                available, so this meeting is not analysed.
              </p>
            </div>
          )}

          {/* Re-run analysis — pm/sa/admin/leadership when a transcript is linked */}
          {canAnalyze && meeting?.transcript_s3_key && (
            <div>
              <p className="mb-1 text-xs font-medium uppercase tracking-wide text-text-secondary">
                Analysis
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => reanalyze.mutate()}
                disabled={reanalyze.isPending}
              >
                {reanalyze.isPending ? (
                  <Loader2
                    className="h-3.5 w-3.5 animate-spin"
                    aria-hidden="true"
                  />
                ) : (
                  <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                )}
                {reanalyze.isPending
                  ? "Triggering…"
                  : meeting.cia_result
                    ? "Re-run Analysis"
                    : "Run Analysis"}
              </Button>
            </div>
          )}

          {/* Expected topics — always shown when the meeting has a CASDM checklist */}
          {meeting.meeting_topics.length > 0 && (
            <div className="space-y-3 rounded-lg border border-outlines bg-subtle-bg p-4">
              <div className="flex items-center gap-2">
                <Brain className="h-4 w-4 text-primary" aria-hidden="true" />
                <span className="text-sm font-semibold text-text-primary">
                  {cia ? "Meeting Intelligence" : "Expected Topics"}
                </span>
                {cia && (
                  <>
                    <Badge
                      variant={
                        SENTIMENT_DISPLAY[cia.sentiment as SentimentSignal]
                          ?.variant ?? "neutral"
                      }
                    >
                      {SENTIMENT_DISPLAY[cia.sentiment as SentimentSignal]
                        ?.label ?? cia.sentiment}
                    </Badge>
                    <span className="text-xs text-text-secondary">
                      {Math.round(cia.confidence * 100)}% confidence
                    </span>
                  </>
                )}
              </div>

              {/* Build the merged topic list */}
              {(() => {
                const covered = new Set(cia?.topics_covered ?? []);
                const missing = new Set(cia?.topics_missing ?? []);
                const allTopics = meeting.meeting_topics;

                return (
                  <ul className="space-y-1.5">
                    {allTopics.map((topic) => {
                      const isCovered = covered.has(topic);
                      const isMissing = missing.has(topic);
                      return (
                        <li
                          key={topic}
                          className="flex items-start gap-2 text-sm"
                        >
                          <span
                            className={cn(
                              "mt-1 h-2 w-2 shrink-0 rounded-full",
                              isCovered
                                ? "bg-success"
                                : isMissing
                                  ? "bg-warning"
                                  : "bg-outlines",
                            )}
                            aria-hidden="true"
                          />
                          <span
                            className={cn(
                              isCovered
                                ? "text-text-primary"
                                : isMissing
                                  ? "text-text-primary"
                                  : "text-text-secondary",
                            )}
                          >
                            {topic}
                          </span>
                          {isCovered && (
                            <span className="ml-auto text-xs text-success">
                              Covered
                            </span>
                          )}
                          {isMissing && (
                            <span className="ml-auto text-xs text-warning">
                              Missing
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                );
              })()}

              {/* CIA extra observations/topics not in the expected list */}
              {cia &&
                cia.topics_covered.filter(
                  (t) => !meeting.meeting_topics.includes(t),
                ).length > 0 && (
                  <div>
                    <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-text-secondary">
                      Also Covered
                    </p>
                    <ul className="space-y-1">
                      {cia.topics_covered
                        .filter((t) => !meeting.meeting_topics.includes(t))
                        .map((t) => (
                          <li
                            key={t}
                            className="flex items-start gap-2 text-sm text-text-primary"
                          >
                            <span
                              className="mt-1 h-2 w-2 shrink-0 rounded-full bg-success"
                              aria-hidden="true"
                            />
                            {t}
                          </li>
                        ))}
                    </ul>
                  </div>
                )}

              {/* Key observations */}
              {cia && cia.key_observations.length > 0 && (
                <div>
                  <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-text-secondary">
                    Key Observations
                  </p>
                  <ul className="list-inside list-disc space-y-1 text-sm text-text-primary">
                    {cia.key_observations.map((o) => (
                      <li key={o}>{o}</li>
                    ))}
                  </ul>
                </div>
              )}

              {cia && (
                <p className="text-xs text-text-secondary">
                  Analyzed {formatDate(cia.analyzed_at)}
                </p>
              )}

              {!cia && meeting.status !== "completed" && (
                <p className="text-xs text-text-secondary">
                  Analysis available after meeting is completed and transcript
                  is uploaded.
                </p>
              )}
              {!cia && meeting.status === "completed" && (
                <p className="text-xs text-text-secondary">
                  Transcript not yet analyzed.
                </p>
              )}
            </div>
          )}

          {/* Fallback for meetings with no topic checklist but CIA result exists */}
          {meeting.meeting_topics.length === 0 && cia && (
            <div className="space-y-4 rounded-lg border border-outlines bg-subtle-bg p-4">
              <div className="flex items-center gap-3">
                <Brain className="h-4 w-4 text-primary" aria-hidden="true" />
                <span className="text-sm font-semibold text-text-primary">
                  Meeting Intelligence
                </span>
                <Badge
                  variant={
                    SENTIMENT_DISPLAY[cia.sentiment as SentimentSignal]
                      ?.variant ?? "neutral"
                  }
                >
                  {SENTIMENT_DISPLAY[cia.sentiment as SentimentSignal]?.label ??
                    cia.sentiment}
                </Badge>
                <span className="text-xs text-text-secondary">
                  {Math.round(cia.confidence * 100)}% confidence
                </span>
              </div>

              {cia.topics_covered.length > 0 && (
                <div>
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-text-secondary">
                    Topics Covered
                  </p>
                  <ul className="space-y-1">
                    {cia.topics_covered.map((t) => (
                      <li
                        key={t}
                        className="flex items-start gap-2 text-sm text-text-primary"
                      >
                        <span
                          className="mt-1 h-2 w-2 shrink-0 rounded-full bg-success"
                          aria-hidden="true"
                        />
                        {t}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {cia.topics_missing.length > 0 && (
                <div>
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-text-secondary">
                    Topics Missing
                  </p>
                  <ul className="space-y-1">
                    {cia.topics_missing.map((t) => (
                      <li
                        key={t}
                        className="flex items-start gap-2 text-sm text-text-primary"
                      >
                        <span
                          className="mt-1 h-2 w-2 shrink-0 rounded-full bg-warning"
                          aria-hidden="true"
                        />
                        {t}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {cia.key_observations.length > 0 && (
                <div>
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-text-secondary">
                    Key Observations
                  </p>
                  <ul className="list-inside list-disc space-y-1 text-sm text-text-primary">
                    {cia.key_observations.map((o) => (
                      <li key={o}>{o}</li>
                    ))}
                  </ul>
                </div>
              )}

              <p className="text-xs text-text-secondary">
                Analyzed {formatDate(cia.analyzed_at)}
              </p>
            </div>
          )}

          {/* Pending analysis state for meetings with no topics and not yet analyzed */}
          {meeting.meeting_topics.length === 0 &&
            !cia &&
            meeting.status === "completed" && (
              <div className="rounded-lg border border-outlines bg-subtle-bg p-4">
                <div className="flex items-center gap-2 text-sm text-text-secondary">
                  <Brain className="h-4 w-4" aria-hidden="true" />
                  Analysis pending — transcript processing in progress
                </div>
              </div>
            )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

type LinkKind = "avoma" | "external";

/**
 * One dialog, one input, one affordance — extended (V037) with a link-type choice so a PM can file
 * an Outlook/Teams/Zoom invite when no Avoma recording exists. The choice selects WHICH field is
 * PATCHed; it does not add a second entry point, which would break the single-affordance model the
 * mutual-exclusivity rule depends on (architecture note §0.6).
 */
function AddLinkDialog({
  projectId,
  meeting,
  open,
  onOpenChange,
}: {
  projectId: string;
  meeting: ProjectMeeting | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<LinkKind>("avoma");
  const [url, setUrl] = useState("");
  const [formError, setFormError] = useState("");

  const isAvoma = kind === "avoma";

  function reset(): void {
    setUrl("");
    setFormError("");
    setKind("avoma");
  }

  const save = useMutation({
    mutationFn: async (): Promise<void> => {
      // Exactly one of the two fields is ever sent. The API rejects both-at-once (400) and rejects
      // an external link on a meeting that already has a genuine Avoma link (409 LINK_CONFLICT).
      const body = isAvoma
        ? { avoma_link: url.trim() }
        : { external_link_url: url.trim() };
      await client.patch(
        `/api/projects/${projectId}/meetings/${meeting!.id}`,
        body,
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["project-meetings", projectId],
      });
      toast.success(isAvoma ? "Avoma link saved" : "External link saved");
      reset();
      onOpenChange(false);
    },
    onError: (err) => setFormError(errorMessage(err, "Failed to save link")),
  });

  function handleSubmit(e: React.FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setFormError("");
    const value = url.trim();
    if (!value) {
      setFormError(
        isAvoma
          ? "Paste an Avoma meeting URL."
          : "Paste the external meeting URL.",
      );
      return;
    }
    // Mirror the two server-side rules so the common mistakes are caught before a round trip.
    // The server remains the enforcer — this is UX only.
    if (!/^https:\/\//i.test(value)) {
      setFormError("The link must start with https://");
      return;
    }
    if (isAvoma && !/(^|\.)avoma\.com$/i.test(hostnameOf(value))) {
      setFormError(
        'That is not an Avoma URL. Choose "External meeting link" for an Outlook, Teams, Zoom, Meet or Webex invite.',
      );
      return;
    }
    save.mutate();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add meeting link</DialogTitle>
          <DialogDescription>{meeting?.meeting_name}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">Link type</legend>
            {(
              [
                {
                  value: "avoma" as LinkKind,
                  label: "Avoma recording",
                  hint: "Fetches the transcript and runs analysis.",
                },
                {
                  value: "external" as LinkKind,
                  label: "External meeting link",
                  hint: "Outlook, Teams, Zoom, Meet or Webex. Stored as a reference only — no transcript, no analysis.",
                },
              ] as const
            ).map((opt) => (
              <label
                key={opt.value}
                htmlFor={`link-kind-${opt.value}`}
                className="flex cursor-pointer items-start gap-2 text-sm"
              >
                <input
                  id={`link-kind-${opt.value}`}
                  type="radio"
                  name="link-kind"
                  value={opt.value}
                  checked={kind === opt.value}
                  onChange={() => {
                    setKind(opt.value);
                    setFormError("");
                  }}
                  className="mt-1"
                />
                <span>
                  <span className="font-medium">{opt.label}</span>
                  <span className="block text-[11px] text-text-secondary">
                    {opt.hint}
                  </span>
                </span>
              </label>
            ))}
          </fieldset>

          <div className="space-y-1.5">
            <Label htmlFor="meeting-url">
              {isAvoma ? "Avoma meeting URL" : "External meeting URL"}
            </Label>
            <Input
              id="meeting-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder={
                isAvoma
                  ? "https://app.avoma.com/meetings/…"
                  : "https://teams.microsoft.com/l/meetup-join/…"
              }
              autoComplete="off"
            />
          </div>
          {formError && (
            <p className="text-sm text-error" role="alert">
              {formError}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending && (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              )}
              Save
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Hostname of a URL string, or "" when it does not parse. Used for the client-side Avoma check. */
function hostnameOf(value: string): string {
  try {
    return new URL(value).hostname.toLowerCase();
  } catch {
    return "";
  }
}

// ─── Avoma title ─────────────────────────────────────────────────────────────

/**
 * AvomaTitle — shows the filled Avoma meeting title (name_template) with a one-click copy button.
 * Users need this exact string to match meetings in Avoma for auto-sync.
 */
function AvomaTitle({ value }: { value: string }): JSX.Element {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div
      className="flex min-w-0 items-center gap-1.5 overflow-hidden rounded-md border border-dashed border-outlines bg-subtle-bg/50 px-2 py-1"
      title="Avoma meeting title — use this exact string when naming the meeting in Avoma"
    >
      <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-text-secondary">
        {value}
      </span>
      <button
        type="button"
        onClick={handleCopy}
        aria-label="Copy Avoma title"
        className="shrink-0 rounded p-0.5 transition-colors hover:bg-primary-subtle focus:outline-none focus-visible:ring-1"
      >
        {copied ? (
          <Check className="h-3 w-3 text-success" aria-hidden="true" />
        ) : (
          <Copy className="h-3 w-3 text-text-secondary" aria-hidden="true" />
        )}
      </button>
    </div>
  );
}

// ─── Meeting card ────────────────────────────────────────────────────────────

function MeetingCard({
  meeting,
  canManage,
  onAddLink,
  onViewDetail,
}: {
  meeting: ProjectMeeting;
  canManage: boolean;
  onAddLink: (m: ProjectMeeting) => void;
  onViewDetail: (m: ProjectMeeting) => void;
}): JSX.Element {
  const status = STATUS_DISPLAY[meeting.status] ?? {
    label: meeting.status,
    variant: "neutral" as BadgeVariant,
  };
  const isGate = meeting.meeting_type === "gate";
  const date = formatDate(meeting.meeting_date);
  // `meeting` satisfies TeamsDeepLinkInput structurally — no cast, no shared-type dependency (§5.1).
  const teamsLink = buildTeamsDeepLink(meeting);

  return (
    <li
      className="flex cursor-pointer items-start justify-between gap-3 rounded-lg border border-outlines p-3 transition-colors hover:bg-subtle-bg"
      onClick={() => onViewDetail(meeting)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onViewDetail(meeting)}
      aria-label={`View details for ${meeting.meeting_name}`}
    >
      <div className="min-w-0 space-y-1.5">
        <div className="flex items-center gap-2">
          {isGate && (
            <Shield
              className="h-4 w-4 shrink-0 text-primary"
              aria-label="Gate meeting"
            />
          )}
          <span
            className={`font-medium text-text-primary ${status.strike ? "line-through" : ""}`}
          >
            {meeting.meeting_name}
          </span>
          {meeting.cia_result && (
            <Brain
              className="h-3.5 w-3.5 text-primary"
              aria-label="Analysis available"
            />
          )}
        </div>
        {/* Avoma title — exact meeting name to use in Avoma for auto-sync */}
        {meeting.name_template && <AvomaTitle value={meeting.name_template} />}
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={meeting.scope === "customer" ? "info" : "neutral"}>
            {meeting.scope === "customer" ? "Customer" : "Internal"}
          </Badge>
          <Badge variant={status.variant}>{status.label}</Badge>
          {meeting.cia_result && (
            <Badge
              variant={SENTIMENT_DISPLAY[meeting.cia_result.sentiment].variant}
            >
              {SENTIMENT_DISPLAY[meeting.cia_result.sentiment].label}
            </Badge>
          )}
          {date && (
            <span className="flex items-center gap-1 text-xs text-text-secondary">
              <CalendarClock className="h-3.5 w-3.5" aria-hidden="true" />
              {date}
            </span>
          )}
        </div>
      </div>

      <div
        className="flex shrink-0 items-center gap-2"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {teamsLink && (
          <Tooltip text={teamsLink.label}>
            <a
              href={teamsLink.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={teamsLink.label}
              className="inline-flex items-center rounded p-1 text-primary transition-colors hover:bg-primary-subtle focus:outline-none focus-visible:ring-1"
            >
              <TeamsIcon />
            </a>
          </Tooltip>
        )}

        {/*
          Display precedence (architecture note §0.6): avoma_link wins when both are set. The Avoma
          sync can populate avoma_link on a row that already carries a manual external link, and in
          that case the recording is the better artifact — the external invite is superseded.
          The two links are labelled DIFFERENTLY on purpose: an external link triggers none of the
          Avoma behaviour (no uuid, no transcript, no CIA), so calling it "Avoma" would misrepresent
          what the user gets.
        */}
        {meeting.avoma_link ? (
          <a
            href={meeting.avoma_link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
          >
            <ExternalLink className="h-4 w-4" aria-hidden="true" />
            Avoma
          </a>
        ) : meeting.external_link_url ? (
          <a
            href={meeting.external_link_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
          >
            <ExternalLink className="h-4 w-4" aria-hidden="true" />
            External link
          </a>
        ) : (
          canManage && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onAddLink(meeting)}
            >
              <Link2 className="h-4 w-4" aria-hidden="true" />
              Add Link
            </Button>
          )
        )}
      </div>
    </li>
  );
}

// ─── Component ─────────────────────────────────────────────────────────────────

export function MeetingTimeline({
  projectId,
}: MeetingTimelineProps): JSX.Element {
  const client = useApiClient();
  const { user } = useAuth();
  const canManage = WRITE_ROLES.includes(user?.role ?? "");
  const { data: project } = useProject(projectId) as {
    data: Project | undefined;
  };
  const canCreate = canActAsMember(user?.role, CREATE_ROLES, project);
  const [newMeetingOpen, setNewMeetingOpen] = useState(false);

  const [linkTarget, setLinkTarget] = useState<ProjectMeeting | null>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [detailTarget, setDetailTarget] = useState<ProjectMeeting | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const query = useQuery({
    queryKey: ["project-meetings", projectId],
    queryFn: async (): Promise<ProjectMeeting[]> => {
      const response = await client.get<{ meetings: ProjectMeeting[] }>(
        `/api/projects/${projectId}/meetings`,
      );
      return response.data.meetings;
    },
    enabled: Boolean(projectId),
  });

  const [syncing, setSyncing] = useState(false);

  // Sync completion arrives via WebSocket (avoma_sync push → useWebSocket dispatchToCache invalidates
  // ["project-meetings", projectId] and shows a toast). No client-side polling.
  const handleSync = async (): Promise<void> => {
    if (syncing) return;
    setSyncing(true);
    try {
      await client.post(`/api/projects/${projectId}/meetings/sync`);
      toast.info("Sync started — you'll be notified when complete");
    } catch {
      toast.error("Failed to start sync");
    } finally {
      setSyncing(false);
    }
  };

  function openAddLink(meeting: ProjectMeeting): void {
    setLinkTarget(meeting);
    setLinkOpen(true);
  }

  const syncButton = canManage ? (
    <Button
      size="sm"
      variant="outline"
      onClick={() => void handleSync()}
      disabled={syncing}
    >
      {syncing ? (
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      ) : (
        <RefreshCw className="h-4 w-4" aria-hidden="true" />
      )}
      {syncing ? "Syncing…" : "Sync"}
    </Button>
  ) : undefined;

  function shell(children: React.ReactNode): JSX.Element {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
          <h3 className="flex items-center gap-2 text-base font-semibold text-text-primary">
            <CalendarClock
              className="h-4 w-4 text-primary"
              aria-hidden="true"
            />
            Meetings
          </h3>
          <div className="flex items-center gap-2">
            {canCreate && (
              <Button
                size="sm"
                onClick={() => setNewMeetingOpen(true)}
                aria-haspopup="dialog"
              >
                <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                {newMeetingMessages.actions.newMeeting}
              </Button>
            )}
            {syncButton}
          </div>
        </CardHeader>
        <CardContent>{children}</CardContent>
      </Card>
    );
  }

  if (query.isLoading) {
    return shell(
      <div className="space-y-3" role="status" aria-label="Loading meetings">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>,
    );
  }

  if (query.isError) {
    return shell(
      <div className="flex flex-col items-start gap-3">
        <p className="text-sm text-error">Failed to load meetings.</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void query.refetch()}
        >
          Retry
        </Button>
      </div>,
    );
  }

  // Defensively hide gate meeting rows (task 2a) — after V028 they no longer exist in the DB.
  const meetings = (query.data ?? []).filter((m) => !isGateMeeting(m));

  function mainCard(): JSX.Element {
    if (meetings.length === 0) {
      return shell(
        <p className="text-sm text-text-secondary">No meetings found</p>,
      );
    }

    // Group by phase (known phases in order, then any others); canonical CASDM order within a phase.
    const byPhase = new Map<string, ProjectMeeting[]>();
    for (const m of meetings) {
      const list = byPhase.get(m.phase) ?? [];
      list.push(m);
      byPhase.set(m.phase, list);
    }
    const phases = [
      ...PHASE_ORDER.filter((p) => byPhase.has(p)),
      ...[...byPhase.keys()].filter((p) => !PHASE_ORDER.includes(p)),
    ];

    return shell(
      <div className="space-y-6">
        {phases.map((phase) => {
          const phaseMeetings = [...(byPhase.get(phase) ?? [])].sort(
            compareMeetingsInPhase,
          );
          return (
            <section key={phase} aria-label={phaseLabel(phase)}>
              <h4 className="mb-2 text-sm font-semibold text-text-primary">
                {phaseLabel(phase)}
              </h4>
              <ul className="space-y-2">
                {phaseMeetings.map((m) => (
                  <MeetingCard
                    key={m.id}
                    meeting={m}
                    canManage={canManage}
                    onAddLink={openAddLink}
                    onViewDetail={(meeting) => {
                      setDetailTarget(meeting);
                      setDetailOpen(true);
                    }}
                  />
                ))}
              </ul>
            </section>
          );
        })}
      </div>,
    );
  }

  return (
    <div className="space-y-6">
      {mainCard()}

      {canCreate && (
        <NewMeetingDialog
          projectId={projectId}
          open={newMeetingOpen}
          onOpenChange={setNewMeetingOpen}
        />
      )}

      {canManage && (
        <AddLinkDialog
          projectId={projectId}
          meeting={linkTarget}
          open={linkOpen}
          onOpenChange={(next) => {
            setLinkOpen(next);
            if (!next) setLinkTarget(null);
          }}
        />
      )}

      <MeetingDetailModal
        meeting={detailTarget}
        open={detailOpen}
        projectId={projectId}
        userRole={user?.role ?? ""}
        onOpenChange={(next) => {
          setDetailOpen(next);
          if (!next) setDetailTarget(null);
        }}
      />
    </div>
  );
}

export default MeetingTimeline;
